/**
 * The look of riding the water mount `bamboo-log` (H20). The long log lies under the rider's feet (ground layer, drawn
 * before the figures), the very long pole and the ripples are drawn after them (world layer). The rider stands in the
 * ride stance; the pole is poled on a 2 s loop (render/motions/bambooPole.ts). Works for both sexes and all 8
 * headings (the log follows `iso.heading`). FX pixel rules: 1-px dots on the art grid, ramp tones, no glow, 12 fps steps.
 */
import type { Entity } from '@nine/shared';
import { polePose, POLE_PERIOD } from '../motions/bambooPole';
import { registerRenderPass, type PassFrame } from './registry';

const LOG = ['#d9d8a0', '#b9c07c', '#8fa05a', '#5f7a3e', '#3d5530']; // pale tan-green -> shade -> outline
const WATER = ['#d7efef', '#9fd0d6', '#6aa7b8']; // ripple ramp
const LOG_LEN = 40; // art px: longer than the figure is tall
const POLE_LEN = 52;

interface Ring {
  x: number;
  y: number;
  t: number;
}
interface State {
  last: Map<number | string, { x: number; y: number; ph: number; moving: number }>;
  rings: Ring[];
}

const isLog = (e: Entity): boolean => e.mount === 'bamboo-log' && !e.dead;

const dot = (g: CanvasRenderingContext2D, x: number, y: number, c: string): void => {
  g.fillStyle = c;
  g.fillRect(Math.round(x), Math.round(y), 1, 1);
};
const line = (g: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, c: string): void => {
  const n = Math.max(1, Math.round(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
  for (let i = 0; i <= n; i++) dot(g, x0 + ((x1 - x0) * i) / n, y0 + ((y1 - y0) * i) / n, c);
};
const ring = (g: CanvasRenderingContext2D, cx: number, cy: number, r: number, c: string, every: number): void => {
  const n = Math.max(8, Math.round(r * 5));
  for (let i = 0; i < n; i += every) {
    const a = (i / n) * Math.PI * 2;
    dot(g, cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.5, c);
  }
};

/** Screen-space frame of the log: foot point, forward unit (flattened), side unit. */
function frame(f: PassFrame, e: Entity) {
  const [sx, sy] = f.iso.proj(e.x, e.y);
  const ha = (f.iso.heading(e) * Math.PI) / 4;
  const ux = Math.cos(ha);
  const uy = Math.sin(ha) * 0.5;
  const l = Math.hypot(ux, uy);
  return { x: Math.round(sx + f.ox), y: Math.round(sy + f.oy), ux: ux / l, uy: uy / l, px: -uy / l, py: ux / l };
}

registerRenderPass<State>({
  id: 'bambooLogLog',
  layer: 'ground',
  order: 231,
  state: () => ({ last: new Map(), rings: [] }),
  draw(f, st) {
    const g = f.g;
    g.save();
    g.globalAlpha = 1;
    for (const e of f.world.entities.values()) {
      if (!isLog(e)) continue;
      const fr = frame(f, e);
      const bob = Math.round(Math.sin(f.world.time * 2.2 + (typeof e.id === 'number' ? e.id : 0)) * 0.6);
      const at = (d: number, side = 0): [number, number] => [
        fr.x + fr.ux * d + fr.px * side,
        fr.y + bob + fr.uy * d + fr.py * side,
      ];
      // The body of the log: 3 rows (light top, mid, shade), nodes every 9 px, round-capped ends.
      for (let d = -LOG_LEN / 2; d <= LOG_LEN / 2; d++) {
        const end = Math.abs(d) >= LOG_LEN / 2 - 1;
        const node = Math.abs(((d + 100) % 9) - 0) < 1;
        for (let r = -1; r <= 1; r++) {
          if (end && r !== 0) continue;
          const [x, y] = at(d, r * 1.4);
          const c = node ? LOG[3]! : r < 0 ? LOG[0]! : r === 0 ? LOG[1]! : LOG[2]!;
          dot(g, x, y - (r === 0 ? 0 : 0), c);
        }
      }
      // A 1-px outline under the log (the water side).
      for (let d = -LOG_LEN / 2; d <= LOG_LEN / 2; d += 1) {
        const [x, y] = at(d, 3);
        dot(g, x, y, LOG[4]!);
      }
      // Ripples at the ends while it moves (12 fps steps).
      const key = e.id;
      const prev = st.last.get(key);
      const moved = prev ? Math.hypot(e.x - prev.x, e.y - prev.y) > 1e-4 : false;
      const moving = moved || (prev?.moving ?? 0) > 0;
      st.last.set(key, { x: e.x, y: e.y, ph: 0, moving: moved ? 6 : Math.max(0, (prev?.moving ?? 0) - 1) });
      if (moving) {
        const age = (f.world.time * 1.5) % 1;
        for (const end of [-1, 1]) {
          const [x, y] = at((end * LOG_LEN) / 2 + end * 2);
          ring(g, x, y + 1, 2 + age * 5, WATER[age < 0.5 ? 0 : 1]!, age < 0.5 ? 1 : 2);
        }
      }
    }
    g.restore();
  },
});

registerRenderPass<State>({
  id: 'bambooLogPole',
  layer: 'world',
  order: 120,
  state: () => ({ last: new Map(), rings: [] }),
  draw(f, st) {
    const g = f.g;
    g.save();
    g.globalAlpha = 1;
    for (const e of f.world.entities.values()) {
      if (!isLog(e)) continue;
      const fr = frame(f, e);
      const ph = (f.world.time / POLE_PERIOD + (typeof e.id === 'number' ? e.id * 0.13 : 0)) % 1;
      const ps = polePose(Math.floor(ph * 24) / 24);
      const tip = (a: number, b: number): [number, number] => [fr.x + fr.ux * a + fr.px * b, fr.y + fr.uy * a + fr.py * b];
      const [tx, ty0] = tip(ps.a, ps.b);
      const ty = ty0 - ps.h;
      // Hands about chest height, leaning with the push.
      const lean = ps.lean * 2;
      const hx = fr.x + fr.ux * lean + fr.px * 3;
      const hy = fr.y + fr.uy * lean - 15;
      // The pole runs from the tip through the hands and on past them, POLE_LEN long in all.
      const dx = hx - tx;
      const dy = hy - ty;
      const dl = Math.max(1, Math.hypot(dx, dy));
      const ex = tx + (dx / dl) * POLE_LEN;
      const ey = ty + (dy / dl) * POLE_LEN;
      line(g, tx + 1, ty + 1, ex + 1, ey + 1, LOG[4]!);
      line(g, tx, ty, ex, ey, LOG[1]!);
      for (let n = 1; n < 6; n++) dot(g, tx + ((ex - tx) * n) / 6, ty + ((ey - ty) * n) / 6, LOG[3]!);
      // A dip: a ripple ring each time the tip meets the water on the way down.
      const was = st.last.get(e.id);
      const down = ps.h < 0.5;
      if (down && !(was?.moving)) st.rings.push({ x: tx, y: ty0, t: f.world.time });
      st.last.set(e.id, { x: 0, y: 0, ph, moving: down ? 1 : 0 });
    }
    // Pole-tip ripple rings that were spawned by the pole pass.
    for (let i = st.rings.length - 1; i >= 0; i--) {
      const r = st.rings[i]!;
      const age = f.world.time - r.t;
      if (age > 0.8 || age < -1) {
        st.rings.splice(i, 1);
        continue;
      }
      const k = Math.floor(age * 12) / 12;
      ring(g, r.x, r.y, 1.5 + k * 9, WATER[k < 0.3 ? 0 : k < 0.55 ? 1 : 2]!, k < 0.3 ? 1 : 2);
    }
    g.restore();
  },
});
